import { useState, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Navbar, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Check, ChevronDown, ArrowLeft, User, Building2, Camera, X, Pencil, Plus,
  Facebook, Instagram, Linkedin, MessageCircle, TreePine,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { provinces, provinceKommunMap } from "@/lib/provinceKommunData";
import { filterCategories } from "@/lib/filterData";
import {
  countries, businessForms, industries, employeeRanges,
  isFreeEmailDomain, isValidOrgNumberForCountry,
} from "@/lib/companyData";
import { ContactPersonModal, ContactPerson } from "@/components/ContactPersonModal";
import { AppleSignInButton } from "@/components/AppleSignInButton";
import { checkRegistrationAvailability, firstTakenErrorMessage } from "@/lib/registrationChecks";
import {
  slugifyUsername, isUsernameAvailable, isValidUsernameFormat,
  generateUniqueUsername, USERNAME_MIN, USERNAME_MAX,
} from "@/lib/usernameUtils";
import { useEffect } from "react";
import { useSeo } from "@/hooks/useSeo";

type AccountType = "personal" | "company" | null;

export default function Register() {
  useSeo({
    title: "Registrera dig",
    description: "Skapa ett konto på TimberNet för att köpa och sälja virke, ved och skogsprodukter.",
    path: "/registrera",
  });
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [accountType, setAccountType] = useState<AccountType>(null);

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <div className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-2xl">
          {accountType === null && (
            <AccountTypePicker onPick={setAccountType} />
          )}
          {accountType === "personal" && (
            <PersonalRegistration onBack={() => setAccountType(null)} signUp={signUp} navigate={navigate} />
          )}
          {accountType === "company" && (
            <CompanyRegistration onBack={() => setAccountType(null)} signUp={signUp} navigate={navigate} />
          )}
        </div>
      </div>
      <Footer hideOnMobile />
    </div>
  );
}

/* ----------------------- Account Type Picker ----------------------- */

function AccountTypePicker({ onPick }: { onPick: (t: AccountType) => void }) {
  return (
    <div>
      <h1 className="mb-2 text-center font-display text-2xl font-bold">Registrera dig</h1>
      <p className="mb-8 text-center text-sm text-muted-foreground">Välj kontotyp för att komma igång</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => onPick("personal")}
          className="group flex flex-col items-center rounded-2xl border-2 border-border bg-card p-8 text-center transition-all hover:border-primary hover:shadow-md"
        >
          <div className="mb-4 rounded-full bg-primary/10 p-4 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
            <User className="h-8 w-8" />
          </div>
          <h2 className="mb-2 font-display text-lg font-semibold">Privatkonto</h2>
          <p className="text-sm text-muted-foreground">För privatpersoner som vill köpa eller sälja virke</p>
        </button>
        <button
          type="button"
          onClick={() => onPick("company")}
          className="group flex flex-col items-center rounded-2xl border-2 border-border bg-card p-8 text-center transition-all hover:border-primary hover:shadow-md"
        >
          <div className="mb-4 rounded-full bg-primary/10 p-4 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
            <Building2 className="h-8 w-8" />
          </div>
          <h2 className="mb-2 font-display text-lg font-semibold">Företagskonto</h2>
          <p className="text-sm text-muted-foreground">För företag och organisationer inom skogsbranschen</p>
        </button>
      </div>
      <div className="my-6 flex items-center gap-3">
        <div className="h-px flex-1 bg-border" />
        <span className="text-xs uppercase text-muted-foreground">eller</span>
        <div className="h-px flex-1 bg-border" />
      </div>
      <AppleSignInButton className="w-full" label="Registrera med Apple" />
      <p className="mt-6 text-center text-sm text-muted-foreground">
        Har du redan ett konto? <Link to="/logga-in" className="font-medium text-primary hover:underline">Logga in</Link>
      </p>
    </div>
  );
}

/* ----------------------- Personal Registration (existing flow, simplified) ----------------------- */

function PersonalRegistration({ onBack, signUp, navigate }: any) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [username, setUsername] = useState("");
  const [usernameStatus, setUsernameStatus] = useState<
    "idle" | "checking" | "available" | "taken" | "invalid"
  >("idle");
  const [password, setPassword] = useState("");
  const [province, setProvince] = useState("");
  const [kommun, setKommun] = useState("");
  const [provinceOpen, setProvinceOpen] = useState(false);
  const [kommunOpen, setKommunOpen] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [locationError, setLocationError] = useState("");

  const kommuner = useMemo(() => (province ? provinceKommunMap[province] ?? [] : []), [province]);

  // Debounced username availability check
  useEffect(() => {
    if (!username) { setUsernameStatus("idle"); return; }
    if (!isValidUsernameFormat(username)) { setUsernameStatus("invalid"); return; }
    setUsernameStatus("checking");
    const t = setTimeout(async () => {
      const ok = await isUsernameAvailable(username);
      setUsernameStatus(ok ? "available" : "taken");
    }, 350);
    return () => clearTimeout(t);
  }, [username]);

  const handleUsernameChange = (raw: string) => {
    // Coerce input to URL-safe form as the user types
    setUsername(slugifyUsername(raw));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!province || !kommun) {
      setLocationError("Välj ditt län och din kommun.");
      return;
    }
    if (!agreed) { toast.error("Du måste godkänna villkoren"); return; }
    if (password.length < 8) { toast.error("Lösenordet måste vara minst 8 tecken"); return; }
    const phoneTrimmed = phone.trim();
    if (!phoneTrimmed) { toast.error("Ange ditt telefonnummer."); return; }
    if (!/^[+\d][\d\s\-()]{6,}$/.test(phoneTrimmed)) { toast.error("Ange ett giltigt telefonnummer."); return; }
    if (!username || !isValidUsernameFormat(username)) {
      toast.error("Välj ett giltigt användarnamn (minst 2 tecken, endast a–z, 0–9 och bindestreck)."); return;
    }
    if (usernameStatus === "taken") {
      toast.error("Det här användarnamnet är redan taget."); return;
    }

    setLoading(true);
    try {
      const availability = await checkRegistrationAvailability({
        full_name: name,
        email,
        phone: phoneTrimmed,
      });
      const takenMsg = firstTakenErrorMessage(availability);
      if (takenMsg) {
        setLoading(false);
        toast.error(takenMsg);
        return;
      }
      // Re-confirm username availability right before signup
      const stillFree = await isUsernameAvailable(username);
      if (!stillFree) {
        setLoading(false);
        setUsernameStatus("taken");
        toast.error("Det här användarnamnet är redan taget.");
        return;
      }
    } catch (err) {
      console.error("Availability check failed", err);
    }

    const { error } = await signUp(email, password, {
      full_name: name,
      account_type: "personal",
      phone: phoneTrimmed,
      location: `${kommun}, ${province}`,
      province,
      municipality: kommun,
      username,
    });
    setLoading(false);
    if (error) toast.error(error.message);
    else {
      toast.success("Konto skapat! Kontrollera din e-post för att verifiera ditt konto.");
      navigate("/logga-in");
    }
  };

  return (
    <div className="mx-auto max-w-sm">
      <button onClick={onBack} className="mb-4 inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="mr-1 h-4 w-4" /> Välj annan kontotyp
      </button>
      <h1 className="mb-2 text-center font-display text-2xl font-bold">Privatkonto</h1>
      <p className="mb-6 text-center text-sm text-muted-foreground">Skapa ditt TimberNet-konto</p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="name">Namn</Label>
          <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Förnamn Efternamn" required />
        </div>
        <div>
          <Label htmlFor="email">E-post</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="din@email.se" required />
        </div>
        <div>
          <Label htmlFor="phone">Telefonnummer</Label>
          <Input id="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="070-123 45 67" required />
        </div>
        <div>
          <Label htmlFor="username">Användarnamn</Label>
          <div className="relative">
            <Input
              id="username"
              value={username}
              onChange={(e) => handleUsernameChange(e.target.value)}
              placeholder="erik-lindqvist"
              minLength={USERNAME_MIN}
              maxLength={USERNAME_MAX}
              autoCapitalize="none"
              autoComplete="off"
              required
              aria-invalid={usernameStatus === "taken" || usernameStatus === "invalid"}
              className={cn(
                usernameStatus === "available" && "border-primary focus-visible:ring-primary/40",
                (usernameStatus === "taken" || usernameStatus === "invalid") && "border-destructive focus-visible:ring-destructive/40",
              )}
            />
            {usernameStatus === "available" && (
              <Check className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-primary" />
            )}
          </div>
          {usernameStatus === "checking" && (
            <p className="mt-1 text-xs text-muted-foreground">Kontrollerar tillgänglighet…</p>
          )}
          {usernameStatus === "available" && (
            <p className="mt-1 text-xs text-primary">Användarnamnet är tillgängligt.</p>
          )}
          {usernameStatus === "taken" && (
            <p className="mt-1 text-xs text-destructive">Det här användarnamnet är redan taget.</p>
          )}
          {usernameStatus === "invalid" && username.length > 0 && (
            <p className="mt-1 text-xs text-destructive">Endast små bokstäver, siffror och bindestreck (minst 2 tecken).</p>
          )}
          {!username && (
            <p className="mt-1 text-xs text-muted-foreground">Din profil-URL: timbernet.se/saljare/{username || "..."}</p>
          )}
        </div>
        <div>
          <Label htmlFor="password">Lösenord</Label>
          <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Minst 8 tecken" required />
        </div>

        <div>
          <Label>Län</Label>
          <Popover open={provinceOpen} onOpenChange={setProvinceOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" role="combobox" className="w-full justify-between font-normal" type="button">
                {province || "Välj län"}
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
              <Command>
                <CommandInput placeholder="Sök län..." />
                <CommandList>
                  <CommandEmpty>Inget län hittades.</CommandEmpty>
                  <CommandGroup>
                    {provinces.map((p) => (
                      <CommandItem key={p} value={p} onSelect={() => {
                        setProvince(p); setKommun(""); setLocationError(""); setProvinceOpen(false);
                      }}>
                        <Check className={cn("mr-2 h-4 w-4", province === p ? "opacity-100" : "opacity-0")} />
                        {p}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {province && (
          <div>
            <Label>Kommun</Label>
            <Popover open={kommunOpen} onOpenChange={setKommunOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" className="w-full justify-between font-normal" type="button">
                  {kommun || "Välj kommun"}
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Sök kommun..." />
                  <CommandList>
                    <CommandEmpty>Ingen kommun hittades.</CommandEmpty>
                    <CommandGroup>
                      {kommuner.map((k) => (
                        <CommandItem key={k} value={k} onSelect={() => {
                          setKommun(k); setLocationError(""); setKommunOpen(false);
                        }}>
                          <Check className={cn("mr-2 h-4 w-4", kommun === k ? "opacity-100" : "opacity-0")} />
                          {k}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        )}

        {locationError && <p className="text-xs text-destructive">{locationError}</p>}

        <div className="flex items-start gap-2">
          <Checkbox id="terms" checked={agreed} onCheckedChange={(v) => setAgreed(v === true)} />
          <label htmlFor="terms" className="text-xs text-muted-foreground leading-snug">
            Jag godkänner <Link to="/villkor" className="underline">villkoren</Link> och <Link to="/integritetspolicy" className="underline">integritetspolicyn</Link>
          </label>
        </div>
        <Button className="w-full" type="submit" disabled={loading}>
          {loading ? "Skapar konto…" : "Skapa konto"}
        </Button>
      </form>
    </div>
  );
}

/* ----------------------- Company Registration (multi-step) ----------------------- */

interface CompanyState {
  // Step 1
  company_name: string;
  organization_number: string;
  country: string;
  business_email: string;
  password: string;
  confirm_password: string;
  agreed: boolean;
  // Step 2
  province: string;
  municipality: string;
  business_form: string;
  industry: string[];
  founded_year: string;
  employee_count: string;
  website: string;
  product_categories: string[];
  // Step 3
  avatar_file: File | null;
  avatar_preview: string | null;
  cover_file: File | null;
  cover_preview: string | null;
  facebook_url: string;
  instagram_url: string;
  linkedin_url: string;
  whatsapp_number: string;
  skogsforum_url: string;
  contact_persons: ContactPerson[];
}

const initialCompanyState: CompanyState = {
  company_name: "", organization_number: "", country: "Sverige",
  business_email: "", password: "", confirm_password: "", agreed: false,
  province: "", municipality: "", business_form: "", industry: [],
  founded_year: "", employee_count: "", website: "", product_categories: [],
  avatar_file: null, avatar_preview: null, cover_file: null, cover_preview: null,
  facebook_url: "", instagram_url: "", linkedin_url: "",
  whatsapp_number: "", skogsforum_url: "", contact_persons: [],
};

function CompanyRegistration({ onBack, signUp, navigate }: any) {
  const [step, setStep] = useState(1);
  const [data, setData] = useState<CompanyState>(initialCompanyState);
  const [loading, setLoading] = useState(false);
  const totalSteps = 3;

  const update = (patch: Partial<CompanyState>) => setData((d) => ({ ...d, ...patch }));

  const next = () => setStep((s) => Math.min(totalSteps, s + 1));
  const back = () => {
    if (step === 1) onBack();
    else setStep((s) => s - 1);
  };

  const validateStep1 = (): string | null => {
    if (!data.company_name.trim()) return "Ange företagsnamn.";
    if (!isValidOrgNumberForCountry(data.organization_number, data.country))
      return data.country === "Sverige"
        ? "Ange ett giltigt organisationsnummer (XXXXXX-XXXX)."
        : "Ange ett giltigt organisationsnummer.";
    if (!data.country) return "Välj land.";
    if (!data.business_email.trim()) return "Ange företagets e-postadress.";
    if (isFreeEmailDomain(data.business_email)) return "Använd din företagets e-postadress.";
    if (data.password.length < 8) return "Lösenordet måste vara minst 8 tecken.";
    if (data.password !== data.confirm_password) return "Lösenorden matchar inte.";
    if (!data.agreed) return "Du måste godkänna villkoren.";
    return null;
  };

  const validateStep2 = (): string | null => {
    if (!data.province) return "Välj län.";
    if (!data.municipality) return "Välj kommun.";
    if (!data.business_form) return "Välj företagsform.";
    if (data.industry.length === 0) return "Välj minst en bransch.";
    return null;
  };

  const handleNext = async () => {
    const err = step === 1 ? validateStep1() : step === 2 ? validateStep2() : null;
    if (err) { toast.error(err); return; }

    // On step 1, verify uniqueness of email, company name & org number against the database.
    if (step === 1) {
      try {
        const availability = await checkRegistrationAvailability({
          email: data.business_email,
          company_name: data.company_name,
          organization_number: data.organization_number,
        });
        const takenMsg = firstTakenErrorMessage(availability);
        if (takenMsg) { toast.error(takenMsg); return; }
      } catch (e) {
        console.error("Availability check failed", e);
      }
    }

    next();
  };

  const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

  const uploadCompanyImage = async (userId: string, file: File, kind: "avatar" | "cover") => {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      throw new Error("Endast bilder (JPG, PNG, WEBP, GIF) tillåts.");
    }
    const safeExt = file.type === "image/jpeg" ? "jpg"
      : file.type === "image/png" ? "png"
      : file.type === "image/webp" ? "webp"
      : "gif";
    const path = `${userId}/${kind}.${safeExt}`;
    const { error } = await supabase.storage.from("avatars").upload(path, file, {
      upsert: true,
      contentType: file.type,
    });
    if (error) throw error;
    const { data } = supabase.storage.from("avatars").getPublicUrl(path);
    return data.publicUrl;
  };

  const completeSocialUrl = (value: string, platform: string): string | null => {
    if (!value) return null;
    if (value.startsWith("http://") || value.startsWith("https://")) return value;
    const baseUrls: Record<string, string> = {
      facebook: "https://www.facebook.com/",
      instagram: "https://www.instagram.com/",
      linkedin: "https://www.linkedin.com/in/",
      whatsapp: "https://wa.me/",
      skogsforum: "https://www.skogsforum.se/members/",
    };
    const handle = platform === "whatsapp" ? value.replace(/[\s-]/g, "") : value;
    return baseUrls[platform] + handle;
  };

  const handleSubmit = async () => {
    setLoading(true);

    // Final uniqueness check before account creation.
    try {
      const availability = await checkRegistrationAvailability({
        email: data.business_email,
        company_name: data.company_name,
        organization_number: data.organization_number,
        phone: data.contact_persons.find((p) => p.phone)?.phone,
      });
      const takenMsg = firstTakenErrorMessage(availability);
      if (takenMsg) {
        setLoading(false);
        toast.error(takenMsg);
        return;
      }
    } catch (e) {
      console.error("Availability check failed", e);
    }

    // Generate a unique username for the company from company_name
    const generatedUsername = await generateUniqueUsername(data.company_name);

    const meta = {
      full_name: data.company_name,
      account_type: "company",
      company_name: data.company_name,
      organization_number: data.organization_number.trim(),
      country: data.country,
      business_email: data.business_email.trim(),
      location: `${data.municipality}, ${data.province}`,
      province: data.province,
      municipality: data.municipality,
      business_form: data.business_form,
      industry: data.industry.length ? data.industry.join(",") : undefined,
      founded_year: data.founded_year || undefined,
      employee_count: data.employee_count || undefined,
      website: data.website || undefined,
      product_categories: data.product_categories.length ? data.product_categories.join(",") : undefined,
      username: generatedUsername,
    };

    const { error, userId } = await signUp(data.business_email.trim(), data.password, meta);
    if (error) {
      setLoading(false);
      toast.error(error.message);
      return;
    }

    // Run post-signup setup via service-role edge function so it works even when
    // there is no session yet (i.e. when email confirmation is required).
    if (userId) {
      try {
        const fileToBase64 = (file: File) =>
          new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const result = reader.result as string;
              resolve(result.split(",")[1] ?? "");
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(file);
          });

        const avatar_base64 = data.avatar_file ? await fileToBase64(data.avatar_file) : null;
        const cover_base64 = data.cover_file ? await fileToBase64(data.cover_file) : null;

        const contact_persons = await Promise.all(
          data.contact_persons.map(async (cp) => ({
            id: cp.id,
            name: cp.name,
            role: cp.role ?? null,
            phone: cp.phone ?? null,
            email: cp.email ?? null,
            photo_base64: cp._file ? await fileToBase64(cp._file) : null,
            photo_mime: cp._file?.type ?? null,
          })),
        );

        const payload = {
          user_id: userId,
          email: data.business_email.trim(),
          avatar_base64,
          avatar_mime: data.avatar_file?.type ?? null,
          cover_base64,
          cover_mime: data.cover_file?.type ?? null,
          socials: {
            facebook_url: completeSocialUrl(data.facebook_url, "facebook"),
            instagram_url: completeSocialUrl(data.instagram_url, "instagram"),
            linkedin_url: completeSocialUrl(data.linkedin_url, "linkedin"),
            whatsapp_number: completeSocialUrl(data.whatsapp_number, "whatsapp"),
            skogsforum_url: completeSocialUrl(data.skogsforum_url, "skogsforum"),
          },
          contact_persons,
        };

        const { error: fnErr } = await supabase.functions.invoke("setup-company-onboarding", {
          body: payload,
        });
        if (fnErr) {
          console.error("setup-company-onboarding error", fnErr);
          toast.error("Kontot skapades men profilbilder/kontaktpersoner kunde inte sparas. Du kan lägga till dem efter inloggning.");
        }
      } catch (e: any) {
        console.error("Post-signup profile setup failed", e);
      }
    }

    setLoading(false);
    toast.success("Företagskonto skapat! Kontrollera företagets e-post för att verifiera kontot.");
    navigate("/logga-in");
  };

  return (
    <div>
      <button onClick={back} className="mb-4 inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="mr-1 h-4 w-4" /> {step === 1 ? "Välj annan kontotyp" : "Tillbaka"}
      </button>
      <h1 className="mb-1 font-display text-2xl font-bold">Företagskonto</h1>
      <p className="mb-2 text-sm text-muted-foreground">Steg {step} av {totalSteps}</p>
      <Progress value={(step / totalSteps) * 100} className="mb-8 h-2" />

      {step === 1 && <CompanyStep1 data={data} update={update} />}
      {step === 2 && <CompanyStep2 data={data} update={update} />}
      {step === 3 && <CompanyStep3 data={data} update={update} />}

      <div className="mt-8 flex justify-between">
        <Button type="button" variant="ghost" onClick={back}>Tillbaka</Button>
        {step < totalSteps ? (
          <Button type="button" onClick={handleNext}>Nästa</Button>
        ) : (
          <Button type="button" onClick={handleSubmit} disabled={loading}>
            {loading ? "Skapar konto…" : "Skapa företagskonto"}
          </Button>
        )}
      </div>
    </div>
  );
}

/* ----------------------- Company Step 1 ----------------------- */

function CompanyStep1({ data, update }: { data: CompanyState; update: (p: Partial<CompanyState>) => void }) {
  const [countryOpen, setCountryOpen] = useState(false);
  const orgNumberError = data.organization_number.length > 0 && !isValidOrgNumberForCountry(data.organization_number, data.country)
    ? (data.country === "Sverige" ? "Format: XXXXXX-XXXX" : "Ogiltigt organisationsnummer")
    : null;
  const emailError = data.business_email.length > 0 && isFreeEmailDomain(data.business_email)
    ? "Använd din företagets e-postadress."
    : null;

  return (
    <div className="space-y-4">
      <h2 className="font-display text-lg font-semibold">Företagsinformation</h2>
      <div>
        <Label>Företagsnamn *</Label>
        <Input value={data.company_name} onChange={(e) => update({ company_name: e.target.value })} required />
      </div>
      <div>
        <Label>Profil-URL</Label>
        <div className="flex items-center rounded-md border border-input bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
          <span className="select-none">timbernet.se/saljare/</span>
          <span className="font-medium text-foreground truncate">
            {slugifyUsername(data.company_name) || "ditt-foretag"}
          </span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Genereras automatiskt från företagsnamnet. Om URL:en redan finns läggs ett nummer till.
        </p>
      </div>
      <div>
        <Label>Organisationsnummer *</Label>
        <Input
          value={data.organization_number}
          onChange={(e) => update({ organization_number: e.target.value })}
          placeholder={data.country === "Sverige" ? "XXXXXX-XXXX" : ""}
          required
        />
        {orgNumberError ? (
          <p className="mt-1 text-xs text-destructive">{orgNumberError}</p>
        ) : (
          <p className="mt-1 text-xs text-muted-foreground">Ditt organisationsnummer används för att verifiera ditt företag.</p>
        )}
      </div>
      <div>
        <Label>Land *</Label>
        <Popover open={countryOpen} onOpenChange={setCountryOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" role="combobox" className="w-full justify-between font-normal" type="button">
              {data.country || "Välj land"}
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
            <Command>
              <CommandInput placeholder="Sök land..." />
              <CommandList>
                <CommandEmpty>Inget land hittades.</CommandEmpty>
                <CommandGroup>
                  {countries.map((c) => (
                    <CommandItem key={c} value={c} onSelect={() => { update({ country: c }); setCountryOpen(false); }}>
                      <Check className={cn("mr-2 h-4 w-4", data.country === c ? "opacity-100" : "opacity-0")} />
                      {c}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
      <div>
        <Label>E-post (företag) *</Label>
        <Input
          type="email"
          value={data.business_email}
          onChange={(e) => update({ business_email: e.target.value })}
          placeholder="kontakt@dittforetag.se"
          required
        />
        {emailError && <p className="mt-1 text-xs text-destructive">{emailError}</p>}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label>Lösenord *</Label>
          <Input
            type="password"
            value={data.password}
            onChange={(e) => update({ password: e.target.value })}
            placeholder="Minst 8 tecken"
            required
          />
        </div>
        <div>
          <Label>Bekräfta lösenord *</Label>
          <Input
            type="password"
            value={data.confirm_password}
            onChange={(e) => update({ confirm_password: e.target.value })}
            required
          />
        </div>
      </div>
      <div className="flex items-start gap-2">
        <Checkbox id="company-terms" checked={data.agreed} onCheckedChange={(v) => update({ agreed: v === true })} />
        <label htmlFor="company-terms" className="text-xs text-muted-foreground leading-snug">
          Jag godkänner <Link to="/villkor" className="underline">villkoren</Link> och <Link to="/integritetspolicy" className="underline">integritetspolicyn</Link>
        </label>
      </div>
    </div>
  );
}

/* ----------------------- Company Step 2 ----------------------- */

function CompanyStep2({ data, update }: { data: CompanyState; update: (p: Partial<CompanyState>) => void }) {
  const [provinceOpen, setProvinceOpen] = useState(false);
  const [kommunOpen, setKommunOpen] = useState(false);
  const [otherOpen, setOtherOpen] = useState(false);
  const kommuner = useMemo(() => (data.province ? provinceKommunMap[data.province] ?? [] : []), [data.province]);

  const toggleCategory = (label: string) => {
    const exists = data.product_categories.includes(label);
    update({
      product_categories: exists
        ? data.product_categories.filter((c) => c !== label)
        : [...data.product_categories, label],
    });
  };

  return (
    <div className="space-y-4">
      <h2 className="font-display text-lg font-semibold">Om företaget</h2>
      <div>
        <Label>Län *</Label>
        <Popover open={provinceOpen} onOpenChange={setProvinceOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" role="combobox" className="w-full justify-between font-normal" type="button">
              {data.province || "Välj län"}
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
            <Command>
              <CommandInput placeholder="Sök län..." />
              <CommandList>
                <CommandEmpty>Inget län hittades.</CommandEmpty>
                <CommandGroup>
                  {provinces.map((p) => (
                    <CommandItem key={p} value={p} onSelect={() => {
                      update({ province: p, municipality: "" }); setProvinceOpen(false);
                    }}>
                      <Check className={cn("mr-2 h-4 w-4", data.province === p ? "opacity-100" : "opacity-0")} />
                      {p}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
      {data.province && (
        <div>
          <Label>Kommun *</Label>
          <Popover open={kommunOpen} onOpenChange={setKommunOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" role="combobox" className="w-full justify-between font-normal" type="button">
                {data.municipality || "Välj kommun"}
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
              <Command>
                <CommandInput placeholder="Sök kommun..." />
                <CommandList>
                  <CommandEmpty>Ingen kommun hittades.</CommandEmpty>
                  <CommandGroup>
                    {kommuner.map((k) => (
                      <CommandItem key={k} value={k} onSelect={() => {
                        update({ municipality: k }); setKommunOpen(false);
                      }}>
                        <Check className={cn("mr-2 h-4 w-4", data.municipality === k ? "opacity-100" : "opacity-0")} />
                        {k}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      )}
      <div>
        <Label>Företagsform *</Label>
        <Select value={data.business_form} onValueChange={(v) => update({ business_form: v })}>
          <SelectTrigger><SelectValue placeholder="Välj företagsform" /></SelectTrigger>
          <SelectContent>
            {businessForms.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Bransch *</Label>
        <p className="mt-1 mb-2 text-xs text-muted-foreground">
          Välj en eller flera branscher som beskriver verksamheten.
        </p>
        <div className="flex flex-wrap gap-2">
          {industries.map((b) => {
            const selected = data.industry.includes(b);
            return (
              <button
                key={b}
                type="button"
                onClick={() =>
                  update({
                    industry: selected
                      ? data.industry.filter((i) => i !== b)
                      : [...data.industry, b],
                  })
                }
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                  selected
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-background text-foreground hover:bg-muted"
                )}
              >
                {b}
                {selected && <X className="h-3 w-3" />}
              </button>
            );
          })}
        </div>
      </div>

      <Collapsible open={otherOpen} onOpenChange={setOtherOpen} className="pt-2">
        <CollapsibleTrigger asChild>
          <Button type="button" variant="ghost" className="px-0 text-primary hover:text-primary">
            <Plus className={cn("mr-1 h-4 w-4 transition-transform", otherOpen && "rotate-45")} />
            Övrig information (valfritt)
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-4 pt-2">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Grundat år</Label>
              <Input
                type="number"
                value={data.founded_year}
                onChange={(e) => update({ founded_year: e.target.value })}
                placeholder="t.ex. 1987"
              />
            </div>
            <div>
              <Label>Antal anställda</Label>
              <Select value={data.employee_count} onValueChange={(v) => update({ employee_count: v })}>
                <SelectTrigger><SelectValue placeholder="Välj" /></SelectTrigger>
                <SelectContent>
                  {employeeRanges.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Webbplats</Label>
            <Input
              value={data.website}
              onChange={(e) => update({ website: e.target.value })}
              placeholder="https://www.dittforetag.se"
            />
          </div>
          <div>
            <Label>Produkter & Kategorier</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {filterCategories.map((c) => {
                const selected = data.product_categories.includes(c.label);
                return (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => toggleCategory(c.label)}
                    className={cn(
                      "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                      selected
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-background text-foreground hover:bg-muted"
                    )}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

/* ----------------------- Company Step 3 ----------------------- */

function CompanyStep3({ data, update }: { data: CompanyState; update: (p: Partial<CompanyState>) => void }) {
  const avatarRef = useRef<HTMLInputElement>(null);
  const coverRef = useRef<HTMLInputElement>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ContactPerson | null>(null);

  const handleAvatar = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; if (!f) return;
    update({ avatar_file: f, avatar_preview: URL.createObjectURL(f) });
  };
  const handleCover = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; if (!f) return;
    update({ cover_file: f, cover_preview: URL.createObjectURL(f) });
  };

  const upsertPerson = (p: ContactPerson) => {
    const exists = data.contact_persons.find((c) => c.id === p.id);
    if (exists) update({ contact_persons: data.contact_persons.map((c) => c.id === p.id ? p : c) });
    else update({ contact_persons: [...data.contact_persons, p] });
  };

  const removePerson = (id: string) => {
    if (!confirm("Ta bort kontaktperson?")) return;
    update({ contact_persons: data.contact_persons.filter((c) => c.id !== id) });
  };

  return (
    <div className="space-y-6">
      <h2 className="font-display text-lg font-semibold">Profil & Kontaktpersoner</h2>

      {/* Cover */}
      <div>
        <Label>Omslagsbild</Label>
        <button
          type="button"
          onClick={() => coverRef.current?.click()}
          className="mt-2 group relative block aspect-[4/1] w-full overflow-hidden rounded-lg border-2 border-dashed border-border bg-muted hover:border-primary transition-colors"
        >
          {data.cover_preview ? (
            <img src={data.cover_preview} alt="Omslag" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              <Camera className="mr-2 h-5 w-5" /> Lägg till omslagsbild
            </div>
          )}
          <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={handleCover} />
        </button>
      </div>

      {/* Avatar */}
      <div>
        <Label>Profilbild (företagslogga)</Label>
        <div className="mt-2 flex items-center gap-4">
          <button
            type="button"
            onClick={() => avatarRef.current?.click()}
            className="relative h-20 w-20 rounded-full ring-2 ring-border hover:ring-primary transition-all"
          >
            <Avatar className="h-20 w-20">
              {data.avatar_preview ? (
                <AvatarImage src={data.avatar_preview} alt="Logga" />
              ) : (
                <AvatarFallback><Camera className="h-6 w-6 text-muted-foreground" /></AvatarFallback>
              )}
            </Avatar>
            <span className="absolute bottom-0 right-0 rounded-full bg-primary p-1 text-primary-foreground">
              <Camera className="h-3 w-3" />
            </span>
            <input ref={avatarRef} type="file" accept="image/*" className="hidden" onChange={handleAvatar} />
          </button>
          <p className="text-sm text-muted-foreground">Ladda upp en logotyp som visas på företagets profil.</p>
        </div>
      </div>

      {/* Social links */}
      <div>
        <Label className="mb-2 block">Sociala länkar</Label>
        <div className="space-y-3">
          <SocialInput icon={Facebook} placeholder="Facebook användarnamn eller URL" value={data.facebook_url} onChange={(v) => update({ facebook_url: v })} />
          <SocialInput icon={Instagram} placeholder="Instagram användarnamn eller URL" value={data.instagram_url} onChange={(v) => update({ instagram_url: v })} />
          <SocialInput icon={Linkedin} placeholder="LinkedIn användarnamn eller URL" value={data.linkedin_url} onChange={(v) => update({ linkedin_url: v })} />
          <SocialInput icon={MessageCircle} placeholder="WhatsApp telefonnummer" value={data.whatsapp_number} onChange={(v) => update({ whatsapp_number: v })} />
          <SocialInput icon={TreePine} placeholder="Skogsforum användarnamn eller URL" value={data.skogsforum_url} onChange={(v) => update({ skogsforum_url: v })} />
        </div>
      </div>

      {/* Contact persons */}
      <div>
        <h3 className="font-sans text-base font-semibold">Kontaktpersoner</h3>
        <p className="mb-3 text-sm text-muted-foreground">Lägg till kontaktpersoner som visas på företagets profil.</p>
        <div className="grid gap-3 sm:grid-cols-2">
          {data.contact_persons.map((p) => {
            const initials = p.name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
            return (
              <div key={p.id} className="relative flex items-center gap-3 rounded-lg border border-border bg-card p-3">
                <Avatar className="h-12 w-12">
                  {p._previewUrl || p.photo_url ? (
                    <AvatarImage src={p._previewUrl ?? p.photo_url ?? undefined} alt={p.name} />
                  ) : (
                    <AvatarFallback>{initials || "?"}</AvatarFallback>
                  )}
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{p.name}</p>
                  {p.role && <p className="truncate text-xs text-muted-foreground">{p.role}</p>}
                </div>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => { setEditing(p); setModalOpen(true); }}
                    className="rounded p-1 text-muted-foreground hover:bg-muted"
                    aria-label="Redigera"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => removePerson(p.id)}
                    className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive"
                    aria-label="Ta bort"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        <Button
          type="button"
          className="mt-3"
          onClick={() => { setEditing(null); setModalOpen(true); }}
        >
          <Plus className="mr-1 h-4 w-4" /> Lägg till person
        </Button>
      </div>

      <ContactPersonModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={upsertPerson}
        initial={editing}
      />
    </div>
  );
}

function SocialInput({ icon: Icon, placeholder, value, onChange }: any) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-muted-foreground" />
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}
